/**
 * Assessment Draft Service
 * =====================================================================
 * Persists an in-progress assessment snapshot to localStorage so that
 * a user who closes the tab or loses their connection mid-assessment
 * can resume where they left off.
 *
 * Data model
 * ----------
 * Key: 'dystest_draft'
 * Value: AssessmentDraft JSON, expires after EXPIRY_DAYS days.
 *
 * Gaze arrays are downsampled to MAX_GAZE_PTS before storing to keep
 * the draft within localStorage budget (~5 MB total limit).
 * pageBackgrounds (large base64 screenshots) are stripped entirely.
 * =====================================================================
 */

import { assessmentCaptureService } from './assessment/captureService';
import { ROUTES } from '../constants/routes';
import { API_CONFIG } from '../constants/app';
import { devWarn } from '../utils/core/devLog';
import { noteEvent } from './exitTrail';

const DRAFT_KEY    = 'dystest_draft';
const EXPIRY_DAYS  = 7;
const MAX_GAZE_PTS = 500;

// ── Step vocabulary ───────────────────────────────────────────────────────────

export type AssessmentStep =
  | 'camera'
  | 'calibration'
  | 'word_icon'
  | 'ran_part'
  | 'ran'
  | 'story1'
  | 'story1_qa'
  | 'story2'
  | 'story2_qa'
  | 'word_writing';

export const STEP_LABEL: Record<AssessmentStep, string> = {
  camera:       'Camera setup',
  calibration:  'Calibration game',
  word_icon:    'Word-icon task',
  ran_part:     'RAN test (some parts done)',
  ran:          'RAN test',
  story1:       'Story 1 reading',
  story1_qa:    'Story 1 questions',
  story2:       'Story 2 reading',
  story2_qa:    'Story 2 questions',
  word_writing: 'Word writing test',
};

// For each completed step, this is the route the user should navigate
// to when they resume. tier === 1 (age 5-7) skips Story 2.
// 'camera' resumes at camera setup again: the camera and eye calibration have to be
// set up afresh in a new session. 'ran_part' (some RAN parts done) restarts RAN.
function resumeRouteAfter(step: AssessmentStep, isTier1: boolean): string {
  switch (step) {
    case 'camera':      return ROUTES.ALLOW_CAMERA;
    case 'calibration': return ROUTES.RAN_TEST;
    case 'word_icon':   return ROUTES.RAN_TEST;
    case 'ran_part':    return ROUTES.RAN_TEST;
    case 'ran':         return ROUTES.STORY;
    case 'story1':      return ROUTES.STORY1_QUESTIONS;
    case 'story1_qa':   return isTier1 ? ROUTES.WORD_WRITING : ROUTES.STORY2;
    case 'story2':      return ROUTES.STORY2_QUESTIONS;
    case 'story2_qa':   return ROUTES.WORD_WRITING;
    case 'word_writing': return ROUTES.FINISH;
  }
}

// ── Draft shape ───────────────────────────────────────────────────────────────

type CaptureSnapshot = ReturnType<typeof assessmentCaptureService.getAllData>;

export interface AssessmentDraft {
  version:        2;
  assessmentId:   string;
  childName:      string;
  childAge:       number | null;
  ageTier:        string;
  email:          string;
  completedSteps: AssessmentStep[];
  lastStep:       AssessmentStep;
  resumeRoute:    string;
  partialData:    CaptureSnapshot;
  savedAt:        string;
  expiresAt:      string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function sampleArray<T>(arr: T[]): T[] {
  if (arr.length <= MAX_GAZE_PTS) return arr;
  const step = Math.ceil(arr.length / MAX_GAZE_PTS);
  return arr.filter((_, i) => i % step === 0);
}

function compressSnapshot(data: CaptureSnapshot): CaptureSnapshot {
  return {
    ...data,
    // Strip large base64 screenshots — not needed for scoring or resuming
    pageBackgrounds: {},
    // Downsample gaze in RAN variants
    ranGazePointsByVariant: Object.fromEntries(
      Object.entries(data.ranGazePointsByVariant ?? {}).map(
        ([k, pts]) => [k, pts ? sampleArray(pts) : pts],
      ),
    ) as CaptureSnapshot['ranGazePointsByVariant'],
    // Downsample gaze in story reading pages
    storyPages: (data.storyPages ?? []).map(({ id, data: sd }) => ({
      id,
      data: { ...sd, gazePoints: sd.gazePoints ? sampleArray(sd.gazePoints) : sd.gazePoints },
    })),
    // Downsample gaze in story answer pages
    storyAnswerPages: (data.storyAnswerPages ?? []).map(({ id, data: ad }) => ({
      id,
      data: {
        ...ad,
        gazePoints: ad.gazePoints ? sampleArray(ad.gazePoints) : [],
      },
    })),
  };
}

function detectCompletedSteps(data: CaptureSnapshot): AssessmentStep[] {
  const steps: AssessmentStep[] = [];
  if (data.allowCamera)                                              steps.push('camera');
  if (data.calibrationGame) { steps.push('calibration'); steps.push('word_icon'); }
  if (data.ranResults && Object.keys(data.ranResults).length > 0)   steps.push('ran');
  if ((data.storyPages ?? []).some(s => s.id.startsWith('story1'))) steps.push('story1');
  if ((data.storyAnswerPages ?? []).some(s => s.id.startsWith('story1'))) steps.push('story1_qa');
  if ((data.storyPages ?? []).some(s => s.id.startsWith('story2'))) steps.push('story2');
  if ((data.storyAnswerPages ?? []).some(s => s.id.startsWith('story2'))) steps.push('story2_qa');
  if ((data.wordWritingResults ?? []).length > 0)                    steps.push('word_writing');
  return steps;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Save the current captureService state as a draft.
 * Called at each major page transition during the assessment.
 * Silent-fails if localStorage is full or unavailable.
 */
export function saveDraft(lastStep: AssessmentStep): void {
  try {
    const snapshot  = assessmentCaptureService.getAllData();
    const userInfo  = assessmentCaptureService.getUserInformation();
    const ageTier   = userInfo?.ageTier ?? '';
    const isTier1   = ageTier === '5-7';

    const expiresAt = new Date(Date.now() + EXPIRY_DAYS * 864e5).toISOString();

    const draft: AssessmentDraft = {
      version:        2,
      assessmentId:   snapshot.assessmentId,
      childName:      userInfo?.name ?? '',
      childAge:       userInfo?.age ?? null,
      ageTier,
      email:          userInfo?.email ?? '',
      completedSteps: detectCompletedSteps(snapshot),
      lastStep,
      resumeRoute:    resumeRouteAfter(lastStep, isTier1),
      partialData:    compressSnapshot(snapshot),
      savedAt:        new Date().toISOString(),
      expiresAt,
    };

    noteEvent(`Progress saved: ${STEP_LABEL[lastStep]}`);
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));

    // Best-effort backend sync so resume links work cross-device.
    // Errors are silent — localStorage remains the primary source of truth.
    if (snapshot.assessmentId) {
      void fetch(`${API_CONFIG.BASE_URL}/assessments/${snapshot.assessmentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'in_progress',
          current_stage: lastStep,
          partial_data: draft,
        }),
      }).catch(() => {});
    }
  } catch (err) {
    // localStorage quota exceeded or unavailable — non-fatal
    devWarn('[DraftService] saveDraft failed:', err);
  }
}

/** Load and validate the stored draft; returns null if absent or expired. */
export function loadDraft(): AssessmentDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as AssessmentDraft;
    if (draft.version !== 2) { clearDraft(); return null; }
    if (new Date(draft.expiresAt) < new Date()) { clearDraft(); return null; }
    if (!draft.assessmentId) return null;
    return draft;
  } catch {
    return null;
  }
}

/** Remove the stored draft (called on successful completion or user choosing "Start fresh"). */
export function clearDraft(): void {
  localStorage.removeItem(DRAFT_KEY);
}

/**
 * After a page reload in the middle of the test (same tab), put the backup back.
 *
 * The results live in memory, so a reload anywhere after Word-Icon used to drop
 * the camera-setup and Word-Icon data while the test carried on, leaving them
 * missing from the final report. Only restores when the draft belongs to the
 * assessment this tab is running (sessionStorage survives a reload, not a new
 * tab) and nothing has been captured in memory yet. Returns true when restored.
 */
export function restoreDraftAfterReload(): boolean {
  try {
    const tabAssessmentId = sessionStorage.getItem('dystest_assessment_id') || sessionStorage.getItem('assessment_id');
    if (!tabAssessmentId) return false;
    const current = assessmentCaptureService.getAllData();
    if (current.allowCamera || current.calibrationGame) return false;
    const draft = loadDraft();
    if (!draft || draft.assessmentId !== tabAssessmentId) return false;
    restoreDraftToCapture(draft);
    return true;
  } catch (err) {
    devWarn('[DraftService] restoreDraftAfterReload failed:', err);
    return false;
  }
}

/**
 * Restore a draft's assessment data into the captureService singleton and
 * repopulate the sessionStorage keys the assessment pages expect.
 */
export function restoreDraftToCapture(draft: AssessmentDraft): void {
  assessmentCaptureService.restoreFromSnapshot(draft.partialData);

  // Repopulate the story-answer sessionStorage key that storyAnswerService reads.
  // Shape: { [pageId]: { answers: string[], backCount: number, submittedAt?: string } }
  try {
    const storyAnswers: Record<string, { answers: string[]; backCount: number; submittedAt?: string }> = {};
    for (const { id, data } of draft.partialData.storyAnswerPages ?? []) {
      storyAnswers[id] = {
        answers: (data.questions ?? []).map((q: { finalAnswer?: string }) => q.finalAnswer ?? ''),
        backCount: data.backCount ?? 0,
      };
    }
    sessionStorage.setItem('dystest_story_answers', JSON.stringify(storyAnswers));
  } catch { /* non-fatal */ }
}
